import {
  useEffect,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode
} from "react";
import { IconSquare, IconSquareCheck } from "@tabler/icons-react";
import { useDrawerInspectorDrillLayout } from "../drawer/DrawerInspectorDrillLayoutContext";
import styles from "./InspectorRow.module.css";

const CHECKBOX_ICON_SIZE = 16;

export type InspectorLabelWeight = "normal" | "medium" | "semibold";

export interface InspectorSectionHeading {
  /** Omit top spacing when this is the first section under the card body. */
  subheadingSpacing?: "first";
}

/** Drawer (or inspector) pseudo-card title row — label only, matches medium-weight row titles. */
interface InspectorRowPanelTitleProps {
  panelTitle: true;
  label: string;
  labelWeight?: InspectorLabelWeight;
  trailing?: undefined;
  sectionHeading?: undefined;
  checkboxId?: undefined;
  checked?: undefined;
  onCheckedChange?: undefined;
  indent?: undefined;
  inspectorSliderTrailing?: undefined;
  cardHeader?: undefined;
}

interface InspectorRowCheckboxProps {
  label: string;
  labelWeight?: InspectorLabelWeight;
  /** Extra inset for grouped rows inside a subsection (matches Figma pl-[4px]). */
  indent?: boolean;
  checkboxId: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /**
   * Value differs from the viewer default. Renders the accent dot that the
   * rail badge summarises, so the user can see WHICH toggle lit the badge.
   */
  modified?: boolean;
  trailing?: undefined;
  sectionHeading?: undefined;
  inspectorSliderTrailing?: undefined;
  cardHeader?: undefined;
}

interface InspectorRowCustomTrailingProps {
  label: string;
  labelWeight?: InspectorLabelWeight;
  trailing: ReactNode;
  /** Card title row — flush with panel horizontal padding (collapse control). */
  cardHeader?: boolean;
  sectionHeading?: undefined;
  checkboxId?: undefined;
  checked?: undefined;
  onCheckedChange?: undefined;
  indent?: undefined;
  inspectorSliderTrailing?: undefined;
}

interface InspectorRowSectionHeaderProps {
  label: string;
  /** Unused — section headers use fixed subheading typography. */
  labelWeight?: InspectorLabelWeight;
  sectionHeading: InspectorSectionHeading;
  trailing?: ReactNode;
  checkboxId?: undefined;
  checked?: undefined;
  onCheckedChange?: undefined;
  indent?: undefined;
  inspectorSliderTrailing?: undefined;
  cardHeader?: undefined;
}

/** Range + boxed value editor (sun month/day/hour). */
export interface InspectorSliderTrailing {
  sliderId: string;
  boxInputId: string;
  ariaLabel: string;
  min: number;
  max: number;
  step: number;
  /** Current canonical numeric value backing the slider. */
  value: number;
  /** Display when the value box is not focused (abbrev month, day digits, formatted hour). */
  boxDisplayValue: string;
  /** Wider left column for long labels (e.g. "Eye height (ft)"). */
  sliderLabelWide?: boolean;
  /** Extra inset for grouped rows (matches Figma pl-[4px]). */
  indent?: boolean;
  onSliderInput: (value: number) => void;
  /** Pointer/touch released after dragging the range thumb. */
  onSliderCommit: () => void;
  /** Interpret the value box contents; invalid input returns undefined. */
  parseBoxValue: (raw: string) => number | undefined;
  /** Canonical number → box text shown when the user focuses the field. */
  formatBoxDraft: (value: number) => string;
  /** Applies a successfully parsed boxed value (`commitOnly` excludes live drag semantics). */
  onBoxParsed: (canonical: number) => void;
}

interface InspectorRowSliderProps {
  label: string;
  labelWeight?: InspectorLabelWeight;
  inspectorSliderTrailing: InspectorSliderTrailing;
  /** Structure body slider — boxed value (32×32) + 16×12 thumb (Figma 241:100). */
  fieldRow?: boolean;
  trailing?: undefined;
  sectionHeading?: undefined;
  checkboxId?: undefined;
  checked?: undefined;
  onCheckedChange?: undefined;
  indent?: undefined;
  cardHeader?: undefined;
}

export type InspectorRowProps =
  | InspectorRowPanelTitleProps
  | InspectorRowCheckboxProps
  | InspectorRowCustomTrailingProps
  | InspectorRowSectionHeaderProps
  | InspectorRowSliderProps;

function SliderValueRow(props: {
  label: string;
  labelClass: string;
  trailing: InspectorSliderTrailing;
  drawerRail: boolean;
  fieldRow: boolean;
}): ReactElement {
  const isl = props.trailing;
  const [boxText, setBoxText] = useState(isl.boxDisplayValue);
  const [boxFocused, setBoxFocused] = useState(false);
  /**
   * Overrides the controlled `value` while the thumb is dragged. Mobile Safari
   * (and the drawer, which does not re-render on every drag tick) otherwise
   * reset the thumb before the parent props catch up.
   */
  const [dragValue, setDragValue] = useState<number | null>(null);

  const canonicalValue = snapRangeValue(
    isl.value,
    isl.min,
    isl.max,
    isl.step
  );
  const rangeValue =
    dragValue !== null
      ? snapRangeValue(dragValue, isl.min, isl.max, isl.step)
      : canonicalValue;

  useEffect(() => {
    if (!boxFocused) {
      setBoxText(isl.boxDisplayValue);
    }
  }, [isl.boxDisplayValue, boxFocused]);

  function handleRangeInput(next: number): void {
    const snapped = snapRangeValue(next, isl.min, isl.max, isl.step);
    setDragValue(snapped);
    isl.onSliderInput(snapped);
  }

  function endRangeDrag(): void {
    setDragValue(null);
    isl.onSliderCommit();
  }

  function commitBox(raw: string): void {
    const parsed = isl.parseBoxValue(raw.trim());
    if (parsed === undefined) {
      setBoxText(isl.boxDisplayValue);
      return;
    }
    isl.onBoxParsed(parsed);
    setBoxFocused(false);
  }

  function stepBox(delta: number): void {
    const parsed = Number.parseFloat(boxText);
    const base =
      boxFocused && Number.isFinite(parsed) ? parsed : canonicalValue;
    const next = snapRangeValue(
      base + delta * isl.step,
      isl.min,
      isl.max,
      isl.step
    );
    isl.onSliderInput(next);
    isl.onBoxParsed(next);
    setBoxText(isl.formatBoxDraft(next));
  }

  const rowClass =
    `${styles.rowSliderLayout}` +
    (isl.indent ? ` ${styles.rowSliderLayoutIndent}` : "") +
    (props.fieldRow ? ` ${styles.rowSliderLayoutField}` : "") +
    (props.drawerRail ? ` ${styles.rowSliderLayoutDrawerRail}` : "");

  const labelColumnClass = `${props.labelClass} ${
    isl.sliderLabelWide ? styles.sliderLabelWide : styles.sliderLabelFixed
  }`;
  const fillPercent =
    isl.max === isl.min
      ? 0
      : ((rangeValue - isl.min) / (isl.max - isl.min)) * 100;
  const rangeStyle = {
    "--slider-fill": `${fillPercent}%`
  } as CSSProperties;
  const valueBoxClass =
    styles.sliderValueBox +
    (props.fieldRow
      ? ` ${
          (boxFocused ? boxText : isl.boxDisplayValue).trim().length <= 1
            ? styles.sliderValueBoxCompact
            : styles.sliderValueBoxWide
        }`
      : "");
  return (
    <div className={rowClass}>
      <span className={labelColumnClass}>
        {props.label}
      </span>
      <div className={styles.sliderRangeWrap}>
        <input
          id={isl.sliderId}
          className={styles.sliderRange}
          type="range"
          aria-label={isl.ariaLabel}
          min={isl.min}
          max={isl.max}
          step={isl.step}
          value={rangeValue}
          style={rangeStyle}
          onInput={(e) => {
            handleRangeInput(Number(e.currentTarget.value));
          }}
          onChange={(e) => {
            handleRangeInput(Number(e.currentTarget.value));
          }}
          onPointerDown={() => {
            setDragValue(canonicalValue);
          }}
          onPointerUp={() => {
            endRangeDrag();
          }}
          onPointerCancel={() => {
            endRangeDrag();
          }}
          onBlur={() => {
            endRangeDrag();
          }}
        />
      </div>
      <input
        id={isl.boxInputId}
        className={valueBoxClass}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        aria-label={`${isl.ariaLabel}, text entry`}
        value={boxText}
        spellCheck={false}
        onChange={(e) => {
          setBoxText(e.target.value);
        }}
        onFocus={(e) => {
          setBoxFocused(true);
          setBoxText(isl.formatBoxDraft(isl.value));
          e.target.select();
        }}
        onBlur={() => {
          commitBox(boxText);
          setBoxFocused(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commitBox(boxText);
            setBoxFocused(false);
            (e.target as HTMLInputElement).blur();
          }
          if (e.key === "ArrowUp") {
            e.preventDefault();
            stepBox(1);
          }
          if (e.key === "ArrowDown") {
            e.preventDefault();
            stepBox(-1);
          }
        }}
      />
    </div>
  );
}

function snapRangeValue(
  value: number,
  min: number,
  max: number,
  step: number
): number {
  if (step <= 0) return value;
  const snapped = Math.round((value - min) / step) * step + min;
  const rounded = Number(snapped.toFixed(8));
  return Math.min(max, Math.max(min, rounded));
}

/**
 * Shared inspector row: checkbox, arbitrary trailing, section subheading, or
 * slider + boxed value controls.
 */
export function InspectorRow(props: InspectorRowProps): ReactElement {
  const drawerRailMatch = useDrawerInspectorDrillLayout();
  const resolvedLabelWeight: InspectorLabelWeight = drawerRailMatch
    ? "normal"
    : (props.labelWeight ?? "medium");
  const weightClass =
    resolvedLabelWeight === "semibold"
      ? styles.labelSemibold
      : resolvedLabelWeight === "medium"
        ? styles.labelMedium
        : styles.labelNormal;
  const labelClass = `${styles.label} ${weightClass}`;

  if ("panelTitle" in props && props.panelTitle) {
    return (
      <div
        className={`${styles.row}${
          drawerRailMatch ? ` ${styles.rowDrawerRail}` : ""
        }`}
      >
        <span className={labelClass}>{props.label}</span>
      </div>
    );
  }

  if ("inspectorSliderTrailing" in props && props.inspectorSliderTrailing) {
    return (
      <SliderValueRow
        label={props.label}
        labelClass={labelClass}
        trailing={props.inspectorSliderTrailing}
        drawerRail={drawerRailMatch}
        fieldRow={props.fieldRow === true}
      />
    );
  }

  if ("checkboxId" in props && props.checkboxId !== undefined) {
    const {
      checkboxId,
      label,
      checked,
      onCheckedChange,
      indent = false,
      modified = false
    } = props;
    const rowClass =
      `${styles.row}${indent ? ` ${styles.rowCheckboxIndent}` : ""}${
        drawerRailMatch ? ` ${styles.rowDrawerRail}` : ""
      }`;
    return (
      <div className={rowClass}>
        <label className={labelClass} htmlFor={checkboxId}>
          {modified ? (
            <span
              className={styles.modifiedDot}
              title="Changed from the default"
              aria-label="Changed from the default"
            />
          ) : null}
          {label}
        </label>
        <span className={styles.trailing}>
          <label htmlFor={checkboxId} className={styles.checkboxControl}>
            <input
              id={checkboxId}
              className={styles.checkboxInput}
              type="checkbox"
              checked={checked}
              onChange={(e) => {
                onCheckedChange(e.target.checked);
              }}
            />
            <span className={styles.checkboxIcon} aria-hidden>
              {checked ? (
                <IconSquareCheck size={CHECKBOX_ICON_SIZE} stroke={1.5} />
              ) : (
                <IconSquare size={CHECKBOX_ICON_SIZE} stroke={1.5} />
              )}
            </span>
          </label>
        </span>
      </div>
    );
  }

  if ("sectionHeading" in props && props.sectionHeading) {
    const { label, sectionHeading, trailing } = props;
    const { subheadingSpacing } = sectionHeading;

    const titleClass = drawerRailMatch
      ? `${styles.sectionHeadingLabelDrawerRail} ${styles.sectionHeadingTitle}` +
        (subheadingSpacing === "first"
          ? ` ${styles.sectionHeadingLabelDrawerRailFirst}`
          : "")
      : `${styles.rowSectionHeadingLabel} ${styles.sectionHeadingTitle}` +
        (subheadingSpacing === "first"
          ? ` ${styles.rowSectionHeadingLabelFirst}`
          : "");

    return (
      <div
        className={`${styles.rowSectionHeading}${
          drawerRailMatch ? ` ${styles.rowSectionHeadingDrawerRail}` : ""
        }`}
      >
        <span className={titleClass}>{label}</span>
        {trailing != null ? (
          <span className={styles.trailing}>{trailing}</span>
        ) : null}
      </div>
    );
  }

  const { label, trailing, cardHeader = false } = props;

  // Card titles read as mono eyebrows (Mono/Eyebrow token) — the "instrument
  // panel" voice — in both the inspector rail and drawer drill-ins.
  const headerLabelClass = cardHeader
    ? `${labelClass} ${styles.labelCardHeader}`
    : labelClass;

  return (
    <div
      className={`${styles.row}${
        cardHeader ? ` ${styles.rowCardHeader}` : ""
      }${drawerRailMatch ? ` ${styles.rowDrawerRail}` : ""}`}
    >
      <span className={headerLabelClass}>{label}</span>
      <span className={styles.trailing}>{trailing}</span>
    </div>
  );
}
